import { useEffect, useState } from "react";
import { Loader2, Save, Check, Send, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui-kit/Card";

function LawPayEditor() {
  const [form, setForm] = useState(null);
  const [tplStage, setTplStage] = useState("initial");
  const [previewSal, setPreviewSal] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testMsg, setTestMsg] = useState(null);
  const [testSendCount, setTestSendCount] = useState(0);
  const [clearBusy, setClearBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [banks, setBanks] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("lawpay") === "connected") return "LawPay connected.";
    if (params.get("lawpay") === "error") return `LawPay connection failed: ${params.get("reason") || "unknown error"}`;
    return "";
  });

  const loadStatus = () => api.get("/lawpay/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));

  useEffect(() => {
    const fallback = {
      lawpay_operating_account_id: "", lawpay_templates: {}, lawpay_final_notice_days: 10,
      lawpay_sig_name: "", lawpay_sig_title: "", lawpay_sig_email: "", lawpay_sig_firm: "", lawpay_sig_tagline: "",
    };
    api.get("/settings").then((r) => {
      setForm({
        lawpay_operating_account_id: r.data.lawpay_operating_account_id || "",
        lawpay_templates: r.data.lawpay_templates || {},
        lawpay_final_notice_days: r.data.lawpay_final_notice_days || 10,
        lawpay_sig_name: r.data.lawpay_sig_name || "",
        lawpay_sig_title: r.data.lawpay_sig_title || "",
        lawpay_sig_email: r.data.lawpay_sig_email || "",
        lawpay_sig_firm: r.data.lawpay_sig_firm || "",
        lawpay_sig_tagline: r.data.lawpay_sig_tagline || "",
      });
      setTestEmail(r.data.lawpay_sig_email || "");
    }).catch(() => setForm(fallback));
    api.get("/lawpay/test-sends").then((r) => setTestSendCount(r.data.count || 0)).catch(() => {});
    loadStatus();
  }, []);

  const connect = async () => {
    setError("");
    try {
      const r = await api.get("/lawpay/oauth/authorize-url");
      window.location.href = r.data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const disconnect = async () => {
    await api.post("/lawpay/disconnect").catch(() => {});
    setBanks(null);
    loadStatus();
  };

  const loadBanks = async () => {
    setError("");
    try {
      const r = await api.get("/lawpay/bank-accounts");
      setBanks(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.put("/settings", { ...form, lawpay_final_notice_days: Number(form.lawpay_final_notice_days) || 10 });
      setSaved(true);
      loadStatus();
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  if (form === null) return <div className="grid place-items-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;

  const ta = "w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const LAWPAY_STAGES = [
    { key: "initial", label: "Initial" }, { key: "d30", label: "30 days" },
    { key: "d60", label: "60 days" }, { key: "d90", label: "90 days" },
    { key: "d90plus", label: "After 90 days" },
  ];
  const setTpl = (stage, field) => (e) => setForm((f) => ({
    ...f,
    lawpay_templates: { ...f.lawpay_templates, [stage]: { ...(f.lawpay_templates?.[stage] || {}), [field]: e.target.value } },
  }));
  const tpl = form.lawpay_templates?.[tplStage] || {};

  const resolveMerge = (text) => {
    const first = "Nancy", last = "Jones";
    const sal = previewSal ? "Mr." : "";
    const greeting = sal && last ? `${sal} ${last}` : [first, last].filter(Boolean).join(" ") || "Client";
    const ctx = {
      greeting_name: greeting, first_name: first, last_name: last, salutation: sal,
      number: "C-001", amount_due: "$1,250.00",
      final_notice_days: String(Number(form.lawpay_final_notice_days) || 10),
    };
    return (text || "").replace(/\{(\w+)\}/g, (m, k) => (k in ctx ? ctx[k] : m));
  };
  const sigBlock = `\n\nBest regards,\n${form.lawpay_sig_name}\n${form.lawpay_sig_title}\n${form.lawpay_sig_email}\n${form.lawpay_sig_firm}\n— ${form.lawpay_sig_tagline} —`;
  const previewSubject = resolveMerge(tpl.subject || "");
  const previewBody = resolveMerge(tpl.body || "") + sigBlock;

  const sendTest = async () => {
    if (!testEmail.trim()) { setTestMsg({ ok: false, text: "Enter an email address to send the test to." }); return; }
    setTestBusy(true); setTestMsg(null);
    try {
      const r = await api.post("/lawpay/test-email", {
        recipient_email: testEmail.trim(), subject: previewSubject, body: previewBody,
      });
      setTestSendCount((n) => n + 1);
      setTestMsg({ ok: true, text: `Test email sent to ${r.data.sent_to}${r.data.test_mode ? " — test mode, no real charge." : "."}` });
    } catch (e) {
      setTestMsg({ ok: false, text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setTestBusy(false); }
  };

  const clearTests = async () => {
    if (typeof window !== "undefined" && !window.confirm("Delete all sandbox test-email invoices this app created from your LawPay account? This won't touch any real client invoices.")) return;
    setClearBusy(true); setTestMsg(null);
    try {
      const r = await api.post("/lawpay/test-sends/clear");
      setTestSendCount(r.data.remaining ?? 0);
      setTestMsg({ ok: true, text: `Deleted ${r.data.deleted} test send${r.data.deleted === 1 ? "" : "s"} from LawPay${r.data.failed ? `; ${r.data.failed} could not be removed.` : "."}` });
    } catch (e) {
      setTestMsg({ ok: false, text: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setClearBusy(false); }
  };
  const connected = status?.send_connected;
  const appConfigured = status?.send_app_configured;
  const expired = status?.send_expired;

  return (
    <div className="space-y-4" data-testid="lawpay-editor">
      {status && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="lawpay-connection-status">
          <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium",
            connected ? "bg-status-paid/10 text-status-paid" : expired ? "bg-destructive/10 text-destructive" : "bg-amber-500/10 text-amber-600")}>
            {connected ? "Invoicing connected" : expired ? "Connection expired" : "Invoicing not connected"}
          </span>
          <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium",
            status.receive_configured ? "bg-status-paid/10 text-status-paid" : "bg-amber-500/10 text-amber-600")}>
            {status.receive_configured ? "Payments webhook ready" : "Webhook not ready"}
          </span>
          <span className="text-muted-foreground">{status.base_url}{status.test_mode ? " · TEST mode" : ""}</span>
        </div>
      )}

      {status && (
        <div className="rounded-md border border-border bg-secondary/40 px-3 py-2.5 text-xs text-muted-foreground" data-testid="lawpay-connection-note">
          <p className="font-medium text-foreground">How the LawPay connection works</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            <li><span className="text-foreground">Sending</span> uses the firm&apos;s authorized connection (OAuth). Tokens can expire — if you see &quot;expired&quot;, click Reconnect.</li>
            <li><span className="text-foreground">Receiving</span> payments relies on LawPay&apos;s webhook. Auto-recording requires the partner app&apos;s <span className="font-medium">Event URL</span> to point to <code className="rounded bg-secondary px-1">{(status.base_url ? "" : "")}your-production-domain/api/webhooks/lawpay</code> — set this at deploy (the preview URL is ephemeral).</li>
            <li>Until the Event URL is set (e.g. in preview), use <span className="font-medium">&quot;Sync from LawPay&quot;</span> on an invoice to pull payment status on demand.</li>
            <li>{status.test_mode
              ? "TEST mode is ON — sends are disarmed against test accounts (this merchant has none, so sends will fail until switched to live at cutover)."
              : "LIVE mode — every payment request is a real charge."}</li>
          </ul>
        </div>
      )}

      {notice && <p className="text-sm text-status-paid" data-testid="lawpay-notice">{notice}</p>}

      {!appConfigured ? (
        <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-foreground" data-testid="lawpay-app-missing">
          Add the LawPay OAuth app credentials (client id/secret + redirect URI) to the backend environment to enable sending payment requests.
        </p>
      ) : expired ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-foreground" data-testid="lawpay-expired">
          <span>LawPay connection expired — reconnect the firm&apos;s account to keep sending payment requests.</span>
          <Button size="sm" onClick={connect} data-testid="lawpay-reconnect">Reconnect LawPay</Button>
        </div>
      ) : connected ? (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">The firm&apos;s LawPay account is authorized.</span>
          <Button variant="ghost" size="sm" onClick={disconnect} data-testid="lawpay-disconnect">Disconnect</Button>
        </div>
      ) : (
        <Button size="sm" onClick={connect} data-testid="lawpay-connect">Connect LawPay</Button>
      )}

      <div>
        <Label htmlFor="lawpay-account">Operating account</Label>
        {connected && (
          <div className="mb-2 flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={loadBanks} data-testid="lawpay-load-banks">Load accounts</Button>
            {banks && (
              <select
                data-testid="lawpay-account-select"
                value={form.lawpay_operating_account_id}
                onChange={set("lawpay_operating_account_id")}
                className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm">
                <option value="">Select an operating account…</option>
                {banks.map((b) => (
                  <option key={b.id} value={b.id} disabled={b.trust_account}>
                    {b.name}{b.trust_account ? " (Trust — not allowed)" : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
        <Input id="lawpay-account" data-testid="lawpay-account-input" value={form.lawpay_operating_account_id} onChange={set("lawpay_operating_account_id")} placeholder="bank_…" className="nums" />
        <p className="mt-1.5 text-xs text-muted-foreground">Client invoice payments are earned fees and route to this OPERATING account — never a trust/IOLTA account.</p>
      </div>
      <div className="space-y-3 rounded-lg border border-border p-3.5" data-testid="lawpay-templates-editor">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label className="mb-0">Payment-request message templates</Label>
          <select
            data-testid="lawpay-template-stage-select"
            value={tplStage}
            onChange={(e) => setTplStage(e.target.value)}
            className="rounded-md border border-input bg-surface px-2.5 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
          >
            {LAWPAY_STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <Label htmlFor="tpl-subject">Subject</Label>
          <Input id="tpl-subject" data-testid="lawpay-template-subject-input" value={tpl.subject || ""} onChange={setTpl(tplStage, "subject")} />
        </div>
        <div>
          <Label htmlFor="tpl-body">Body</Label>
          <textarea id="tpl-body" data-testid="lawpay-template-body-input" rows={8} className={ta} value={tpl.body || ""} onChange={setTpl(tplStage, "body")} />
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Merge fields:{" "}
          {["{greeting_name}", "{first_name}", "{last_name}", "{salutation}", "{number}", "{amount_due}"].map((f) => (
            <code key={f} className="mr-1 rounded bg-secondary px-1">{f}</code>
          ))}
          {tplStage === "d90plus" && <code className="mr-1 rounded bg-secondary px-1">{"{final_notice_days}"}</code>}
          . <code className="rounded bg-secondary px-1">{"{greeting_name}"}</code> renders &quot;Mr. Smith&quot; when the client has a salutation, or &quot;Nancy Jones&quot; when they don&apos;t. The signature block below is appended automatically.
        </p>
        <div className="max-w-[240px]">
          <Label htmlFor="tpl-fnd">Final-notice response window (days)</Label>
          <Input id="tpl-fnd" type="number" min="1" data-testid="lawpay-final-notice-days-input" value={form.lawpay_final_notice_days} onChange={set("lawpay_final_notice_days")} className="nums" />
        </div>
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-secondary/30 p-3.5" data-testid="lawpay-preview-panel">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <Label className="mb-0">Preview — as the client will see it</Label>
            <p className="mt-0.5 text-xs text-muted-foreground">Live sample for <span className="font-medium text-foreground">{LAWPAY_STAGES.find((s) => s.key === tplStage)?.label}</span>, updates as you type.</p>
          </div>
          <div className="inline-flex overflow-hidden rounded-md border border-border" role="group">
            <button type="button" data-testid="lawpay-preview-salutation-on"
              onClick={() => setPreviewSal(true)}
              className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${previewSal ? "bg-primary text-primary-foreground" : "bg-surface text-muted-foreground hover:bg-accent"}`}>
              With salutation
            </button>
            <button type="button" data-testid="lawpay-preview-salutation-off"
              onClick={() => setPreviewSal(false)}
              className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${!previewSal ? "bg-primary text-primary-foreground" : "bg-surface text-muted-foreground hover:bg-accent"}`}>
              No salutation
            </button>
          </div>
        </div>
        <div className="overflow-hidden rounded-md border border-border bg-surface">
          <div className="border-b border-border px-4 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Subject</p>
            <p className="text-sm font-medium text-foreground" data-testid="lawpay-preview-subject">{previewSubject}</p>
          </div>
          <p className="whitespace-pre-line px-4 py-3 text-sm leading-relaxed text-foreground" data-testid="lawpay-preview-body">{previewBody}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
          <div className="min-w-[200px] flex-1">
            <Label htmlFor="lawpay-test-email">Send this as a test email to yourself</Label>
            <Input id="lawpay-test-email" data-testid="lawpay-test-email-input" type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@firm.com" />
          </div>
          <Button type="button" variant="secondary" onClick={sendTest} disabled={testBusy} data-testid="lawpay-test-email-button">
            {testBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test email
          </Button>
        </div>
        <p className="-mt-1 text-xs text-muted-foreground">Sends the preview above (current stage + salutation) through LawPay so you see the real rendering and payment link. No real charge in test mode.</p>
        {testSendCount > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 py-2" data-testid="lawpay-test-sends-row">
            <span className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground" data-testid="lawpay-test-sends-count">{testSendCount}</span> test {testSendCount === 1 ? "invoice" : "invoices"} created in your LawPay test account.
            </span>
            <button type="button" onClick={clearTests} disabled={clearBusy} data-testid="lawpay-clear-test-sends-button"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-destructive transition-colors hover:text-destructive/80 disabled:opacity-50">
              {clearBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Clear test sends
            </button>
          </div>
        )}
        {testMsg && (
          <p className={`text-xs ${testMsg.ok ? "text-status-paid" : "text-destructive"}`} data-testid="lawpay-test-email-result">{testMsg.text}</p>
        )}
      </div>

      <div className="space-y-3 rounded-lg border border-border p-3.5" data-testid="lawpay-signature-editor">
        <Label className="mb-0">Email signature</Label>
        <p className="-mt-1 text-xs text-muted-foreground">Set explicitly here — not pulled from your profile or firm branding. Appended to every message.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label htmlFor="sig-name">Signature name</Label><Input id="sig-name" data-testid="lawpay-sig-name-input" value={form.lawpay_sig_name} onChange={set("lawpay_sig_name")} /></div>
          <div><Label htmlFor="sig-title">Title</Label><Input id="sig-title" data-testid="lawpay-sig-title-input" value={form.lawpay_sig_title} onChange={set("lawpay_sig_title")} /></div>
          <div><Label htmlFor="sig-email">Email</Label><Input id="sig-email" data-testid="lawpay-sig-email-input" value={form.lawpay_sig_email} onChange={set("lawpay_sig_email")} /></div>
          <div><Label htmlFor="sig-firm">Firm name</Label><Input id="sig-firm" data-testid="lawpay-sig-firm-input" value={form.lawpay_sig_firm} onChange={set("lawpay_sig_firm")} /></div>
          <div className="sm:col-span-2"><Label htmlFor="sig-tag">Tagline</Label><Input id="sig-tag" data-testid="lawpay-sig-tagline-input" value={form.lawpay_sig_tagline} onChange={set("lawpay_sig_tagline")} /></div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {saved && <span className="inline-flex items-center gap-1 text-sm text-status-paid"><Check className="h-4 w-4" /> Saved</span>}
        {error && <span className="text-sm text-destructive">{error}</span>}
        <div className="ml-auto">
          <Button size="sm" onClick={save} disabled={saving} data-testid="lawpay-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save LawPay
          </Button>
        </div>
      </div>
    </div>
  );
}

export function LawPayCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>LawPay payments</CardTitle>
        <CardDescription>Collect client invoice payments online via LawPay. Sets the operating account funds route to and the email sent with each payment request.</CardDescription>
      </CardHeader>
      <CardContent><LawPayEditor /></CardContent>
    </Card>
  );
}
